import { cast, flow, Instance, types } from 'mobx-state-tree';
import retry from 'async-retry';
import Common from '@store/common';
import endpoints from '@configs/endpoints';
import axios from 'axios';
import { basePorCobrarUrl, authTokenPorCobrar, baseUrl, appUrl } from '@configs/variables';
import debug from 'debug';
import { sleep } from '@utils/sleep';
import logPayment from '@utils/logPayment';

const error = debug('SuraStore:error');

export enum PaymentMethod {
  card = 'CARD',
  cash = 'CASH',
  spei = 'SPEI',
  directDebit = 'DIRECT_DEBIT',
  bankReference = 'BANK_REFERENCE',
}

export enum PaymentOption {
  visa_mastercard = 'VISA_MASTERCAD',
  american = 'AMERICAN',
  clabe = 'CLABE',
  send_reference = 'SEND_REFERENCE',
  download_reference = 'DOWNLOAD_REFERENCE',
}

export const Sources = {
  share_email: 'SHARE_EMAIL',
  portal_client: 'PORTAL_CLIENT',
  portal_agent: 'PORTAL_AGENT',
};

const ERRORS_TO_RETRY_GET_PROOF_PAYMENT = [
  'DataPolicies does no exist.',
  'Transaction does no exist.',
  'Payment Request does no exist',
];

const SuraStore = types
  .compose(
    Common,
    types.model({
      isSura: types.optional(types.boolean, false),
      paymentMethod: types.optional(types.string, ''),
      step: types.optional(types.number, 0),
      paymentOption: types.optional(types.string, ''),
      baseUrl: types.optional(types.string, ''),
      sendReceipt: types.optional(types.boolean, false),
      proofPayment: types.optional(types.string, ''),
      proofPaymentFileName: types.optional(types.string, ''),
      flag: types.optional(types.boolean, false),
      isSendingEmail: types.optional(types.boolean, false),
      isSendingSms: types.optional(types.boolean, false),
      isPaymentExpired: types.optional(types.boolean, false),
    }),
  )
  .actions((self) => ({
    setFlag(flag: boolean) {
      self.flag = cast(flag);
    },
    setProofPFileName(data: string) {
      self.proofPaymentFileName = cast(data);
    },
    setProofP(data: string) {
      self.proofPayment = cast(data);
    },
  }))
  .actions((self) => ({
    setIsSura(isSura: boolean) {
      self.isSura = isSura;
    },
    setPaymentMethod(paymentMethod: string) {
      self.paymentMethod = cast(paymentMethod);
    },
    setStep(step: number) {
      self.step = step;
    },
    setPaymentOption(paymentOption: string) {
      self.paymentOption = cast(paymentOption);
    },
    setBaseUrl(baseUrl: string) {
      self.baseUrl = cast(baseUrl);
    },
    setSendReceipt(sendReceipt: boolean) {
      self.sendReceipt = sendReceipt;
    },
    setPaymentExpired(isPaymentExpired: boolean) {
      self.isPaymentExpired = isPaymentExpired;
    },
    getProofPayment: flow(function* (externalRefId: string) {
      const that = self;
      self.flag = true;

      yield sleep(5000);
      const { paymentLink } = self.globalStore.links;
      yield retry(
        async (_: any, attempt) => {
          try {
            const { data } = await axios.post(
              `${basePorCobrarUrl}${endpoints.proofPayment}`,
              {
                externalRefId: externalRefId,
              },
              {
                headers: {
                  Authorization: authTokenPorCobrar,
                },
              },
            );
            that.setProofP(data?.data || '');
            that.setProofPFileName(data?.name || '');
            that.setFlag(false);
          } catch (e: any) {
            if (
              e?.response?.data?.code === 400 &&
              ERRORS_TO_RETRY_GET_PROOF_PAYMENT.includes(e?.response?.data?.error)
            ) {
              throw e;
            } else {
              logPayment({
                businessId: paymentLink.businessId,
                parentPartnerId: '',
                type: 'ERROR',
                source: 'AR_FRONT',
                paymentPartner: 'OPENPAY',
                paymentMethod: 'CARD_PAYMENT',
                key: '[AR] - ERROR - PC_SURA_GET_PROOF_PAYMENT',
                code: '05',
                description: `Error when requesting POST ${basePorCobrarUrl}${endpoints.proofPayment}`,
                metadata: JSON.stringify({ externalRefId, error: e.response.data }),
                paymentLinkId: paymentLink.paymentLinkId,
              });
              error('Error obtaining proof of payment', e);
            }
          }
        },
        {
          retries: 10,
          factor: 1,
          minTimeout: 3000, // milliseconds
        },
      ).catch((err) => {
        logPayment({
          businessId: paymentLink.businessId,
          parentPartnerId: '',
          type: 'ERROR',
          source: 'AR_FRONT',
          paymentPartner: 'OPENPAY',
          paymentMethod: 'CARD_PAYMENT',
          key: '[AR] - ERROR - PC_SURA_GET_PROOF_PAYMENT',
          code: '05',
          description: `After 10 attempts, error when requesting POST ${basePorCobrarUrl}${endpoints.proofPayment}`,
          metadata: JSON.stringify({ externalRefId, error: err.response.data }),
          paymentLinkId: paymentLink.paymentLinkId,
        });
        error('Error obtaining proof of payment', err);
      });

      self.flag = false;
    }),
    postSendReceipt: flow(function* (externalRefId: string, type: string, input: string) {
      try {
        if (type === 'email') {
          self.isSendingEmail = true;
        }
        if (type === 'sms') {
          self.isSendingSms = true;
        }
        yield axios.post(
          `${basePorCobrarUrl}${endpoints.sendReceipt}/${type}`,
          {
            externalRefId: externalRefId,
            to: type === 'sms' ? `+52${input}` : input,
            ...(type === 'email' && {
              attachment: self.proofPayment ?? '',
            }),
          },
          {
            headers: {
              Authorization: authTokenPorCobrar,
            },
          },
        );
      } catch (e) {
        const { paymentLink } = self.globalStore.links;
        logPayment({
          businessId: paymentLink.businessId,
          parentPartnerId: '',
          type: 'WARNING',
          source: 'AR_FRONT',
          paymentPartner: 'OPENPAY',
          paymentMethod: 'CARD_PAYMENT',
          key: '[AR] - WARNING - PC_SURA_SEND_RECEIPT',
          code: '06',
          description: `Error when requesting POST ${basePorCobrarUrl}${endpoints.sendReceipt}/${type}. PaymentLinkId: ${paymentLink.paymentLinkId}`,
          metadata: JSON.stringify({ e, externalRefId, type, input }),
          paymentLinkId: paymentLink.paymentLinkId,
        });
        error('Error send receipt', e);
      } finally {
        if (type === 'email') {
          self.isSendingEmail = false;
        }
        if (type === 'sms') {
          self.isSendingSms = false;
        }
      }
    }),
    postBankReference: flow(function* (externalRefId: string, mode: string, input: string) {
      try {
        self.flag = true;
        const { data } = yield axios.post(
          `${basePorCobrarUrl}${endpoints.bankReference}`,
          {
            externalRefId: externalRefId,
            mode: mode,
            ...(mode === 'send' && {
              to: input,
            }),
          },
          {
            headers: {
              Authorization: authTokenPorCobrar,
            },
          },
        );
        if (mode === 'download') {
          const linkSource = `data:application/pdf;base64,${data.data}`;
          const downloadLink = document.createElement('a');
          const fileName = data?.name || `Recibo#${externalRefId}.pdf`;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
        }
      } catch (e) {
        error('Error post bank reference', e);

        const { paymentLink } = self.globalStore.links;
        logPayment({
          businessId: paymentLink.businessId,
          parentPartnerId: '',
          type: 'WARNING',
          source: 'AR_FRONT',
          paymentPartner: 'OPENPAY',
          paymentMethod: 'BANK_REFERENCE_PAYMENT',
          key: '[AR] - WARNING - PC_SURA_POST_BANK_REFERENCE',
          code: '07',
          description: `Error when requesting POST ${basePorCobrarUrl}${endpoints.bankReference}. PaymentLinkId: ${paymentLink.paymentLinkId}`,
          metadata: JSON.stringify({ e, externalRefId, mode, input }),
          paymentLinkId: paymentLink.paymentLinkId,
        });
        self.flag = false;
        throw e;
      } finally {
        self.flag = false;
      }
    }),
    sharePaymentLink: flow(function* (to: string, externalRefId: string, paymentLinkId: string) {
      try {
        self.isSendingEmail = true;
        const response = yield axios.put(`${baseUrl}${endpoints.paymentDueDate(paymentLinkId)}`);
        const url = `${appUrl}/sura/pay/${response.data.newPaymentLinkId}/payment-methods/online-payment`;
        const { status } = yield axios.post(
          `${basePorCobrarUrl}${endpoints.sharePaymentLink}`,
          {
            externalRefId,
            url,
            to,
          },
          {
            headers: {
              Authorization: authTokenPorCobrar,
            },
          },
        );
        self.isSendingEmail = false;
        return status;
      } catch (e) {
        const { paymentLink } = self.globalStore.links;
        logPayment({
          businessId: paymentLink.businessId,
          parentPartnerId: '',
          type: 'WARNING',
          source: 'AR_FRONT',
          paymentPartner: 'OPENPAY',
          paymentMethod: 'CARD_PAYMENT',
          key: '[AR] - ERROR - PC_SURA_SHARE_PAYMENT_LINK',
          code: '08',
          description: `Error when requesting POST ${basePorCobrarUrl}${endpoints.sharePaymentLink}. PaymentLinkId: ${paymentLink.paymentLinkId}`,
          metadata: JSON.stringify({ e, externalRefId, paymentLinkId }),
          paymentLinkId: paymentLink.paymentLinkId,
        });
        error('Error sending payment link url', e);
      } finally {
        self.isSendingEmail = false;
      }
    }),
  }));

export type ISuraStore = Instance<typeof SuraStore>;
export default SuraStore;
